import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  moduleName?: string;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  // Quando true, o erro é de chunk (deploy contínuo invalidou o hash) e estamos
  // recarregando — mostra "Atualizando…" em vez da tela de erro (evita o flash
  // assustador de "Algo deu errado" antes do reload automático).
  reloading: boolean;
}

// Detecta o erro classico de Vite + lazy loading + deploy continuo:
// chunk antigo nao existe mais no servidor apos novo deploy. F5 resolve
// porque baixa o index.html atualizado.
function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const msg = error.message || "";
  return (
    error.name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Loading CSS chunk/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

const CHUNK_RELOAD_KEY = "chunk_load_error_reload_at";
const CHUNK_RELOAD_WINDOW_MS = 30_000;

// Anti-loop: só autoriza reload se o último foi há mais de 30s. Se o erro
// persistir após reload (chunk novo TAMBÉM falha = problema real), retorna false
// pra cair na UI de erro normal em vez de recarregar em loop.
function canAutoReloadOnChunkError(): boolean {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0");
    if (Date.now() - last < CHUNK_RELOAD_WINDOW_MS) {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
      return false;
    }
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage indisponivel — segue sem anti-loop (permite reload)
  }
  return true;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, reloading: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Para chunk error, marca reloading=true JÁ no primeiro render pós-erro —
    // assim a UI mostra "Atualizando…" e nunca a tela de erro (sem flash). O
    // reload em si dispara no componentDidCatch (com anti-loop).
    return { hasError: true, error, reloading: isChunkLoadError(error) };
  }

  componentDidMount() {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    } catch {
      // ignore
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (isChunkLoadError(error)) {
      if (canAutoReloadOnChunkError()) {
        console.warn("[ErrorBoundary] Chunk load error — recarregando para a nova versão");
        window.location.reload();
        return;
      }
      // Anti-loop disparou (chunk novo também falhou): reverte pra UI de erro.
      this.setState({ reloading: false });
      return;
    }
    console.error(
      `[ErrorBoundary${this.props.moduleName ? ` - ${this.props.moduleName}` : ""}]`,
      error,
      errorInfo.componentStack
    );
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, reloading: false });
  };

  render() {
    // Chunk error em recuperação: tela neutra "Atualizando…" (reload iminente).
    if (this.state.reloading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 gap-3 text-center">
          <RefreshCw className="h-8 w-8 text-muted-foreground animate-spin" />
          <p className="text-sm text-muted-foreground">Atualizando para a nova versão…</p>
        </div>
      );
    }

    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[300px] p-8 gap-4 text-center">
          <AlertTriangle className="h-12 w-12 text-destructive" />
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {this.props.moduleName
                ? `Erro no modulo ${this.props.moduleName}`
                : "Algo deu errado"}
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Ocorreu um erro inesperado. Tente novamente ou recarregue a pagina.
            </p>
            {this.state.error && (
              <p className="text-xs text-muted-foreground mt-2 font-mono max-w-md break-all">
                {this.state.error.message}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={this.handleRetry}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Tentar novamente
            </Button>
            <Button variant="default" onClick={() => window.location.reload()}>
              Recarregar pagina
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
